/**
 * Virtual Deliverability Manager — a switch that puts a surcharge on somebody
 * else's AWS bill, so the price is the first thing on it.
 *
 * ## The price is not in a tooltip
 *
 * About **$0.07 per 1,000 messages**, against the roughly **$0.10 per 1,000**
 * Amazon already charges to send — a surcharge near 70% of the send cost, on
 * the tenant's own AWS bill under bring-your-own-SES. That is stated in the
 * paragraph above the control, again as figures beside each other, and a third
 * time inside the confirmation, and it is the reason the confirmation cannot be
 * completed without ticking an acknowledgement.
 *
 * Three statements of the same number is not redundancy to be trimmed. The
 * paragraph is what somebody reads while deciding; the figures are what makes
 * "$0.07" legible as a *proportion* rather than as a small number; the
 * acknowledgement is what makes the consent a record rather than a belief. A
 * design review that folds these into one will have removed the second and
 * third, and the second and third are the ones that do the work.
 *
 * ## The acknowledgement is enforced by the contract, not by this component
 *
 * `vdmUpdateSchema`'s enabled arm requires `costAcknowledged: z.literal(true)`.
 * A browser literally cannot express "switch on a surcharge on my AWS bill
 * without having been shown the price" — the request body has no shape for it.
 * The checkbox below is therefore not the enforcement; it is the *event* the
 * enforcement records, and it is wired so that the flag can only be produced by
 * a person who has the price on screen in front of them. Defaulting it to
 * ticked, or setting the flag unconditionally at the call site, would leave the
 * schema satisfied and the guarantee gone — which is exactly the shape of bug
 * this arrangement exists to make visible.
 *
 * `evaluateTrackingChange` keeps the *first* `costAcknowledgedAt` when VDM is
 * already on, so re-saving the other switches never rewrites the record of when
 * somebody was actually told. That is why `unchangedUpdate()` may carry
 * `costAcknowledged: true` through an unrelated save without this component
 * being involved.
 *
 * ## WHAT THIS SWITCH ACTUALLY DOES, WHICH IS LESS THAN IT USED TO CLAIM
 *
 * It records that the tenant wants the figures and accepts the price. It does
 * **not** enable VDM in their AWS account, and nothing in Wend does: VDM is an
 * account-wide Amazon attribute covering every message that account sends —
 * including mail Wend never saw, under bring-your-own-SES — and
 * `ses.provisioning.ts` puts VDM's own attributes on the console side of the
 * line it draws between what Wend provisions and what it leaves alone.
 *
 * The copy used to say Amazon starts charging from the moment this switch moves.
 * It does not, because nothing had been enabled, so the consent recorded here
 * was consent to a charge that never arrived in exchange for a panel that could
 * never fill in — and no screen named the console step that would have fixed it.
 * The confirmation now says who enables it and where, `VDM_ENABLE_STEP` in
 * `@wend/shared` is the one wording of that step, and the empty per-provider
 * panel repeats it rather than telling somebody to wait.
 *
 * ## What it is not
 *
 * VDM's metrics are aggregate, dimensioned by identity, configuration set and
 * ISP, with no contact identity in them at all. They drive no suppression and
 * build no segment, and `delivery_events` stays the record of what happened to
 * a message. The panel says so, because a tenant reading "deliverability
 * insights" on an AWS page will reasonably assume it tells them which of their
 * members had a problem, and it does not.
 *
 * ## SES has to be connected, and the shared evaluator does not say so
 *
 * `evaluateTrackingChange` gates `needs_ses_connection` on `open || click`, so a
 * VDM-only change with no SES account connected is not refused by it. VDM is a
 * setting inside the tenant's own AWS account and there is nothing to set it on,
 * so this panel refuses that case itself with a plain sentence. That is a
 * frontend gate over a hole in a shared rule, which is the weaker of the two
 * places to put it — recorded in this slice's report so the evaluator can be the
 * one to close it.
 */
import type { TrackingResponse, TrackingSettingsUpdate } from '@wend/shared';
import { Callout, Checkbox, Kicker } from '@wend/ui';
import { useState } from 'react';

import { formatDateTime } from '../../lib/format';
import { DisclosedSwitch } from './DisclosedSwitch';
import { formatPerThousandUsd, unchangedUpdate, vdmSurchargeShare } from './tracking-display';

export interface VdmPanelProps {
  readonly response: TrackingResponse;
  readonly onSave: (update: TrackingSettingsUpdate) => Promise<void>;
  readonly busy?: boolean;
  /** Set when there is no SES account for VDM to be switched on inside. */
  readonly disabledReason: string | null;
}

export function VdmPanel({ response, onSave, busy = false, disabledReason }: VdmPanelProps) {
  const { settings, vdmCost } = response;
  const [acknowledged, setAcknowledged] = useState(false);

  const share = vdmSurchargeShare(vdmCost.perThousandUsd, vdmCost.sendPerThousandUsd);

  return (
    <section className="flex flex-col gap-4 border-t border-line pt-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-[0.98rem] font-medium text-text">Virtual Deliverability Manager</h3>
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          Amazon&rsquo;s own view of how your mail is landing, per receiving provider — Gmail,
          Outlook, the regional hosts your members actually use. It is aggregate: counts by
          provider, with no contact in them. It drives no suppression, builds no segment, and
          nothing in Wend depends on it. What happened to a particular message stays in delivery
          events.
        </p>
      </div>

      {/*
        The figures, beside each other, before the switch. `$0.07` on its own
        reads as nothing; `$0.07 on top of $0.10` reads as what it is.
      */}
      <dl className="flex flex-wrap gap-x-8 gap-y-3 rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
        <CostFigure
          label="VDM, per 1,000 messages"
          value={`about ${formatPerThousandUsd(vdmCost.perThousandUsd)}`}
        />
        <CostFigure
          label="SES sending, per 1,000"
          value={`about ${formatPerThousandUsd(vdmCost.sendPerThousandUsd)}`}
        />
        {share !== null && <CostFigure label="Added to your Amazon bill" value={`about +${share}`} />}
      </dl>

      {disabledReason !== null ? (
        <Callout tone="warn" title="This cannot be switched on yet">
          {disabledReason}
        </Callout>
      ) : (
        <DisclosedSwitch
          title="Deliverability insights"
          on={settings.vdm.enabled}
          // Verbatim from `VDM_COST_DISCLOSURE`, carried on the wire so that the
          // sentence in the API's audit entry and the sentence somebody read are
          // provably the same one.
          disclosure={vdmCost.disclosure}
          turnOnLabel="Turn on deliverability insights"
          confirmLabel="Yes, show me these figures and bill them to my AWS account"
          // Nothing here says "this changes every message you send", because it
          // does not: VDM adds nothing to a message and changes nothing a
          // recipient sees. What it changes is an invoice — and, as the second
          // paragraph now says out loud, only once the tenant has enabled VDM in
          // their own AWS console, because Wend does not do that for them.
          confirmNote={
            <>
              <p>
                This does not change anything inside your messages — no pixel, no rewritten links,
                and nothing a recipient can see. What it changes is what Amazon charges you.
              </p>
              <p className="mt-2">
                Wend does not switch Virtual Deliverability Manager on in your AWS account and
                cannot: it is an account-wide Amazon setting covering every message that account
                sends, including mail that has nothing to do with Wend. This says you want the
                figures. Turning VDM on — and starting the charge — is a step in your own console:
                Amazon SES → Virtual Deliverability Manager → Get started.
              </p>
            </>
          }
          turnOffLabel="Turn off deliverability insights"
          refusal={null}
          busy={busy}
          confirmDisabled={!acknowledged}
          onCancelConfirm={() => {
            setAcknowledged(false);
          }}
          confirmExtra={
            <div className="flex flex-col gap-2">
              <p className="max-w-prose text-[0.92rem] leading-relaxed text-text">
                Amazon charges about {formatPerThousandUsd(vdmCost.perThousandUsd)} per 1,000
                messages
                {share === null ? '' : `, roughly ${share} on top of what sending already costs you`}
                , from the moment Virtual Deliverability Manager is enabled in your AWS account.
                Wend does not bill it and cannot waive it.
              </p>
              <Checkbox
                checked={acknowledged}
                onCheckedChange={setAcknowledged}
                label="I understand Amazon will charge my own AWS account once I enable this there"
              />
            </div>
          }
          onChange={async (next) => {
            await onSave({
              ...unchangedUpdate(settings),
              // The acknowledgement flag is produced here and only here — by a
              // path that ran through a ticked checkbox under the price. It is
              // never spread in from a default.
              vdm: next ? { enabled: true, costAcknowledged: true } : { enabled: false },
            });
            setAcknowledged(false);
          }}
        />
      )}

      {settings.vdm.enabled && (
        <div className="flex flex-col gap-2">
          <p className="text-[0.85rem] text-muted">
            Switched on {formatDateTime(settings.vdm.costAcknowledgedAt)}, against a quoted price of{' '}
            {formatPerThousandUsd(settings.vdm.costPerThousandUsdAtAcknowledgement)} per
            1,000. Amazon&rsquo;s list price is what it is today; this is what was on the screen when
            it was accepted.
          </p>
          {/*
            The two things that have to be true for the panel to have anything in
            it, said here rather than left for the empty panel to say later.
            Both are outside Wend's control and neither is obvious: Wend does not
            enable VDM in somebody else's AWS account, and Amazon reports these
            figures against the configuration set, which a message only goes
            through when open or click tracking is on.
          */}
          <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
            Two things have to be true before figures appear. Virtual Deliverability Manager has to
            be enabled in your AWS account — Amazon SES console &rarr; Virtual Deliverability
            Manager &rarr; Get started; Wend does not do this for you. And open or click tracking
            has to be on, because Amazon reports these figures against the configuration set your
            messages are sent through, and with both switches off they are not sent through it.
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * One figure with its unit spelled out in the label rather than appended to the
 * number, so that "about $0.07" is never read as a total.
 */
function CostFigure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <Kicker as="dt">{label}</Kicker>
      <dd className="text-[0.95rem] text-text">{value}</dd>
    </div>
  );
}
